/**
 * The Funnel map: the whiteboard funnel, drawn top to bottom with live
 * numbers (GET /api/command/funnel-map).
 *
 *   Traffic sources → Visited syncsuite.co → Opened signup → Signed up
 *   (Free / Pro door) → Watched the video → Paying for Pro → the offer ladder (from Customers → Funnel → Steps' offer ladder).
 *   Agencies get their own lane: their video. Offers with a code are counted
 *   (AI-Ready Day = attended; Done-for-you month / Mastermind = bought) and
 *   open the people behind them.
 *
 * Filters live in the address (?days, ?source, ?door) so a filtered map can
 * be shared and Back from a person returns to it. Every counted step opens
 * the people behind it. Steps we can't count yet say so instead of showing 0.
 *
 * The connectors are an SVG layer measured from the real card positions, so
 * the layout is plain CSS grid and stays responsive; on phones the agency
 * lane moves below the main lane.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import {
  Globe, MousePointerClick, UserPlus, PlayCircle, CreditCard, Gift, Building2, Clapperboard, Waypoints,
  X, Info, ChevronDown, Lock, Crown, CalendarDays,
} from "lucide-react";
import { Drawer, Skeleton, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Delta, Segmented, dotClass, num, timeAgo, usd } from "@/features/command/ui";

// ─── Data ────────────────────────────────────────────────────────────────────

interface TrafficLane { id: string; slug: string | null; name: string; color: string | null; icon: string | null; visitors: number; signups: number }
interface Offer {
  id: string; name: string; kind: string; price_usd: number; recurring: string | null; feeds_from: string | null; target_rate: number | null; color: string | null;
  /** What counts it: a plan, or an offer code (ai_ready_day, dfy_month, mastermind_10k ...). */
  code: string | null;
  count: number | null;
  /** Done-for-you month only: how many got it with Pro yearly (counted apart from bought). */
  included?: number | null;
}
interface MapData {
  from: string; to: string; days: number | null;
  /** Whether the previous period is fully inside the data (else no arrows). */
  compare: { website: boolean; cohort: boolean };
  source: { id: string; slug: string; name: string; color: string | null } | null;
  door: Door;
  traffic: TrafficLane[];
  visitors: number;
  started_signup: number;
  signups: { total: number; free: number; pro: number; agencies: number };
  video: { started: number; half: number; finished: number; anonymous: number; since: string | null };
  paying: { n: number; revenue: number };
  offers: Offer[];
  prev: { visitors: number; started_signup: number; signups: number; video: number; paying: number };
}
interface SourceLite { id: string; slug: string; name: string; color: string | null; parent_id: string | null }
type Door = "all" | "free" | "pro";
type BaseStep = "signups" | "free" | "pro" | "agencies" | "video" | "video_half" | "video_finished" | "paying";
/** A map step, or an offer by its code ("offer:dfy_month"). */
type Step = BaseStep | `offer:${string}`;
interface Person {
  account_id: string; contact_id: string | null; name: string; email: string | null;
  signed_up_at: string; door: "free" | "pro"; agency: boolean; first_paid_at: string | null; revenue: number;
  video_step: number | null;
  source_name: string | null; source_color: string | null;
}

const PERIODS = [
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "365", label: "12 months" },
  { value: "all", label: "All time" },
] as const;
type Period = (typeof PERIODS)[number]["value"];

const STEP_TITLE: Record<BaseStep, string> = {
  signups: "Signed up", free: "Came in the Free door", pro: "Came in the Pro door", agencies: "Agencies",
  video: "Pressed play on the video", video_half: "Watched half the video", video_finished: "Watched the whole video",
  paying: "Paying for Pro",
};

/** Source colours as stroke colours (same names as the source dots). */
const HEX: Record<string, string> = {
  pink: "#f472b6", violet: "#a78bfa", blue: "#38bdf8", amber: "#fbbf24", zinc: "#a1a1aa", emerald: "#34d399",
  orange: "#fb923c", indigo: "#818cf8", sky: "#38bdf8", fuchsia: "#e879f9", teal: "#2dd4bf", rose: "#fb7185",
  yellow: "#facc15", stone: "#a8a29e", lime: "#a3e635", slate: "#94a3b8", magenta: "#e879f9", aqua: "#22d3ee",
  green: "#34d399", red: "#f87171", brand: "#818cf8",
};
// The Done-for-you month is sold to Pro and Agency customers alike (and comes
// with Pro yearly), so only an offer named for agencies goes in their lane.
const isAgencyOffer = (o: Offer) => /agency/i.test(o.name);

/** The drawer title for a step (an offer's own name for an offer step). */
function stepTitle(step: Step, offers: Offer[]): string {
  if (!step.startsWith("offer:")) return STEP_TITLE[step as BaseStep];
  const code = step.slice("offer:".length);
  if (code === "dfy_month_included") return "Done-for-you month with Pro yearly";
  const o = offers.find((x) => x.code === code);
  return o ? `${o.name}${o.kind === "event" ? ": attended" : ": bought"}` : "Took this offer";
}
const pctLabel = (n: number, of: number) => {
  if (!of) return "–";
  const p = (n / of) * 100;
  if (p > 100) return "100%+";
  return p >= 10 || p === 0 ? `${Math.round(p)}%` : `${p.toFixed(1)}%`;
};
const sinceLabel = (iso: string | null) =>
  iso ? `Counting since ${new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : "Starts counting with the next one";

// ─── The view ────────────────────────────────────────────────────────────────

export function FunnelMapView() {
  const toast = useToast();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const days = (PERIODS.some((p) => p.value === params.get("days")) ? params.get("days") : "30") as Period;
  const source = params.get("source") ?? "";
  const door = (["free", "pro"].includes(params.get("door") ?? "") ? params.get("door") : "all") as Door;
  const [data, setData] = useState<MapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState<SourceLite[]>([]);
  const [people, setPeople] = useState<Step | null>(null);
  const [howOpen, setHowOpen] = useState(false);

  const setParam = (k: string, v: string | null) => {
    const next = new URLSearchParams(params);
    next.set("view", "map");
    if (v == null || v === "" || (k === "days" && v === "30") || (k === "door" && v === "all")) next.delete(k);
    else next.set(k, v);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get<MapData>("/command/funnel-map", { days, ...(source ? { source } : {}), ...(door !== "all" ? { door } : {}) })
      .then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) toast.error(e instanceof ApiError ? e.message : "Couldn't load the funnel"); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, source, door]);

  useEffect(() => {
    api.get<{ sources: SourceLite[] }>("/sources", { lite: 1 }).then((d) => setSources(d.sources)).catch(() => {});
  }, []);

  const sourceOptions = useMemo(() => {
    const kids = (pid: string | null) => sources.filter((s) => s.parent_id === pid);
    const out: { value: string; label: string }[] = [];
    const walk = (pid: string | null, depth: number) => {
      for (const s of kids(pid)) {
        out.push({ value: s.slug, label: `${" ".repeat(depth)}${s.name}` });
        walk(s.id, depth + 1);
      }
    };
    walk(null, 0);
    return out;
  }, [sources]);

  const backState = { from: `${location.pathname}${location.search}` };

  return (
    <div className="space-y-4">
      {/* Filters: one row above the map. */}
      <div className="flex flex-wrap items-center gap-2">
        <Segmented size="sm" value={days} onChange={(v) => setParam("days", v)} options={PERIODS.map((p) => ({ value: p.value, label: p.label }))} />
        <label className="relative inline-flex items-center">
          <Waypoints className="pointer-events-none absolute left-2.5 h-3.5 w-3.5 text-zinc-500" />
          <select
            value={source}
            onChange={(e) => setParam("source", e.target.value || null)}
            aria-label="Traffic source"
            className="h-8 appearance-none rounded-xl border border-zinc-800 bg-zinc-900/60 pl-8 pr-8 text-xs font-medium text-zinc-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
          >
            <option value="">All traffic</option>
            {sourceOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2.5 h-3.5 w-3.5 text-zinc-500" />
        </label>
        <Segmented
          size="sm"
          value={door}
          onChange={(v) => setParam("door", v)}
          options={[{ value: "all", label: "Both doors" }, { value: "free", label: "Free door" }, { value: "pro", label: "Pro door" }]}
        />
        {(source || door !== "all") && (
          <button
            type="button"
            onClick={() => { const n = new URLSearchParams(); n.set("view", "map"); if (days !== "30") n.set("days", days); setParams(n, { replace: true }); }}
            className="inline-flex h-8 items-center gap-1 rounded-xl px-2.5 text-xs font-medium text-zinc-400 transition-colors hover:bg-zinc-800/60 hover:text-zinc-100"
          >
            <X className="h-3.5 w-3.5" /> Clear filters
          </button>
        )}
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setHowOpen((v) => !v)}
          className="inline-flex h-8 items-center gap-1.5 rounded-xl px-2.5 text-xs font-medium text-zinc-400 transition-colors hover:bg-zinc-800/60 hover:text-zinc-100"
          aria-expanded={howOpen}
        >
          <Info className="h-3.5 w-3.5" /> How it's counted
        </button>
      </div>

      {howOpen && (
        <div className="cc-card space-y-1.5 p-4 text-[13px] leading-relaxed text-zinc-300">
          <p><b className="text-zinc-100">Website steps</b> count the people who visited syncsuite.co in the period. Each visit is credited to a source by where it came from, the same way as Traffic sources.</p>
          <p><b className="text-zinc-100">From "Signed up" on</b>, the map follows the people who signed up in the period, whenever they took the next step. So "3 of 9 watched the video" means 3 of those 9 people.</p>
          <p><b className="text-zinc-100">The video</b> counts plays of the quick start video on the signup page and syncsuite.co/start.</p>
          <p>Team and test accounts never count. Steps marked "Not tracked yet" are on your plan but nothing records them yet.</p>
        </div>
      )}

      {loading && !data ? (
        <div className="space-y-4">
          <Skeleton className="h-24 w-full rounded-2xl" />
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="mx-auto h-24 w-full max-w-sm rounded-2xl" />)}
        </div>
      ) : data ? (
        <div className={cn("transition-opacity", loading && "opacity-60")}>
          <MapCanvas data={data} onPeople={setPeople} onPickSource={(slug) => setParam("source", slug)} periodLabel={PERIODS.find((p) => p.value === days)!.label} />
        </div>
      ) : null}

      <PeopleDrawer
        step={people}
        title={people ? stepTitle(people, data?.offers ?? []) : ""}
        onClose={() => setPeople(null)}
        query={{ days, ...(source ? { source } : {}), ...(door !== "all" ? { door } : {}) }}
        backState={backState}
      />
    </div>
  );
}

// ─── The canvas: cards in a grid + measured connectors ──────────────────────

interface Edge {
  from: string;
  to: string;
  /** People flowing along it (sets the thickness); null = not tracked. */
  n: number | null;
  label?: string;
  color?: string;
  dashed?: boolean;
  /** Only drawn when the lanes sit side by side (lg and up). */
  wideOnly?: boolean;
  /** Skips rows: runs down beside the cards instead of behind them. */
  bypass?: boolean;
  /** Only drawn when the lanes are stacked (phones). */
  narrowOnly?: boolean;
}

function MapCanvas({ data, onPeople, onPickSource, periodLabel }: { data: MapData; onPeople: (s: Step) => void; onPickSource: (slug: string | null) => void; periodLabel: string }) {
  const wrap = useRef<HTMLDivElement>(null);
  const nodes = useRef(new Map<string, HTMLElement>());
  const reg = useCallback((id: string) => (el: HTMLElement | null) => { if (el) nodes.current.set(id, el); else nodes.current.delete(id); }, []);
  const [geom, setGeom] = useState<{ d: string; w: number; color: string; dashed: boolean; label?: string; lx: number; ly: number; key: string }[]>([]);
  const [size, setSize] = useState({ w: 0, h: 0 });

  const s = data.signups;
  const prev = data.prev;
  // Everything derived from one load, computed once (the connector layer
  // re-measures whenever these change, so they must be stable).
  const { traffic, moreTraffic, maxFlow, agencyOffers, depthRows } = useMemo(() => {
    const freeOffer = data.offers.find((o) => o.kind === "free" || (o.price_usd === 0 && /free/i.test(o.name))) ?? null;
    const ladder = data.offers.filter((o) => o !== freeOffer);
    const agency = ladder.filter(isAgencyOffer);
    const main = ladder.filter((o) => !isAgencyOffer(o));
    // Depth rows for the main ladder (parent → child via feeds_from).
    const ids = new Set(main.map((o) => o.id));
    const depth = new Map<string, number>();
    const d = (o: Offer, seen = new Set<string>()): number => {
      if (depth.has(o.id)) return depth.get(o.id)!;
      if (seen.has(o.id)) return 0;
      seen.add(o.id);
      const parent = o.feeds_from && ids.has(o.feeds_from) ? main.find((x) => x.id === o.feeds_from) : null;
      const v = parent ? d(parent, seen) + 1 : 0;
      depth.set(o.id, v);
      return v;
    };
    const rows: Offer[][] = [];
    for (const o of main) { const v = d(o); (rows[v] ??= []).push(o); }
    const t = data.traffic.slice(0, 7);
    return {
      traffic: t,
      moreTraffic: data.traffic.length - t.length,
      maxFlow: Math.max(1, data.visitors, ...t.map((x) => x.visitors)),
      agencyOffers: agency,
      depthRows: rows.filter(Boolean),
    };
  }, [data]);
  const offerName = (id: string | null) => (id ? data.offers.find((o) => o.id === id)?.name ?? null : null);

  const edges: Edge[] = useMemo(() => {
    const out: Edge[] = [];
    for (const t of traffic) {
      const c = HEX[t.color ?? ""] ?? "#818cf8";
      if (t.visitors > 0) out.push({ from: `src:${t.id}`, to: "visited", n: t.visitors, color: c, wideOnly: true });
      else if (t.signups > 0) out.push({ from: `src:${t.id}`, to: "signed", n: t.signups, color: c, dashed: true, bypass: true, wideOnly: true });
    }
    // Phones: the source cards wrap into rows, so one line from the whole row.
    if (traffic.length) out.push({ from: "traffic-row", to: "visited", n: data.visitors, narrowOnly: true });
    out.push({ from: "visited", to: "opened", n: data.started_signup, label: pctLabel(data.started_signup, data.visitors) });
    // More signups than signup-page visitors happens (invites, partners, the
    // app): no percentage then, it would read as nonsense.
    out.push({ from: "opened", to: "signed", n: s.total, label: s.total <= data.started_signup ? pctLabel(s.total, data.started_signup) : undefined });
    out.push({ from: "signed", to: "video", n: data.video.started, label: `${pctLabel(data.video.started, s.total)} of signups` });
    out.push({ from: "video", to: "paying", n: data.paying.n, label: `${pctLabel(data.paying.n, s.total)} of signups` });
    // Main ladder: first row hangs off Paying, deeper rows off their parent.
    for (const [i, row] of depthRows.entries()) {
      for (const o of row) {
        const parent = i === 0 ? "paying" : `offer:${o.feeds_from}`;
        out.push({ from: parent, to: `offer:${o.id}`, n: o.count, dashed: o.count == null });
      }
    }
    out.push({ from: "signed", to: "agencies", n: s.agencies, wideOnly: true });
    out.push({ from: "agencies", to: "agency-video", n: null, dashed: true });
    for (const o of agencyOffers) out.push({ from: "agency-video", to: `offer:${o.id}`, n: o.count, dashed: true });
    return out;
  }, [traffic, data, s, depthRows, agencyOffers]);


  // Measure after layout, and again whenever the canvas resizes.
  useLayoutEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const measure = () => {
      const box = el.getBoundingClientRect();
      const wide = window.matchMedia("(min-width: 1024px)").matches;
      const out: typeof geom = [];
      for (const e of edges) {
        if ((e.wideOnly && !wide) || (e.narrowOnly && wide)) continue;
        const a = nodes.current.get(e.from)?.getBoundingClientRect();
        const b = nodes.current.get(e.to)?.getBoundingClientRect();
        if (!a || !b) continue;
        let d: string, lx: number, ly: number;
        const below = b.top >= a.bottom - 2;
        if (e.bypass && below) {
          // Straight down beside the main lane, then into the target's top corner.
          const x1 = a.left + a.width / 2 - box.left, y1 = a.bottom - box.top;
          const tl = b.left - box.left, tr = b.right - box.left, y2 = b.top - box.top;
          const side = x1 >= (tl + tr) / 2 ? 1 : -1;
          const bx = side > 0 ? Math.max(x1, tr + 22) : Math.min(x1, tl - 22);
          const x2 = side > 0 ? tr - 28 : tl + 28;
          const r = 18;
          d = `M ${x1} ${y1} C ${x1} ${y1 + r}, ${bx} ${y1 + r}, ${bx} ${y1 + 2 * r} L ${bx} ${y2 - 2 * r} C ${bx} ${y2 - r}, ${x2} ${y2 - r}, ${x2} ${y2}`;
          lx = bx; ly = (y1 + y2) / 2;
        } else if (below) {
          const x1 = a.left + a.width / 2 - box.left, y1 = a.bottom - box.top;
          const x2 = b.left + b.width / 2 - box.left, y2 = b.top - box.top;
          const my = (y1 + y2) / 2;
          d = `M ${x1} ${y1} C ${x1} ${my}, ${x2} ${my}, ${x2} ${y2}`;
          lx = (x1 + x2) / 2; ly = my;
        } else {
          const right = b.left >= a.right - 2;
          const x1 = (right ? a.right : a.left) - box.left, y1 = a.top + a.height / 2 - box.top;
          const x2 = (right ? b.left : b.right) - box.left, y2 = b.top + b.height / 2 - box.top;
          const mx = (x1 + x2) / 2;
          d = `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
          lx = mx; ly = (y1 + y2) / 2;
        }
        const w = e.n == null || e.n === 0 ? 1.5 : 1.5 + 8 * Math.sqrt(e.n / maxFlow);
        out.push({ d, w, color: e.color ?? "#818cf8", dashed: !!e.dashed || e.n == null, label: e.label, lx, ly, key: `${e.from}>${e.to}` });
      }
      setGeom((prevGeom) => (JSON.stringify(prevGeom) === JSON.stringify(out) ? prevGeom : out));
      setSize((sz) => (sz.w === box.width && sz.h === box.height ? sz : { w: box.width, h: box.height }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, [edges, maxFlow]);

  const videoNote = data.video.anonymous > 0
    ? `+${num(data.video.anonymous)} ${data.video.anonymous === 1 ? "person" : "people"} watched before signing up or from a device we can't match yet`
    : null;
  // Up/down arrows only when last period is a fair comparison. Video plays
  // started counting on a known day: compare only once both periods are after it.
  const trackedSince = (iso: string | null) => !!iso && Date.parse(iso) <= Date.parse(data.from) - (Date.parse(data.to) - Date.parse(data.from));
  const cmp = (kind: "website" | "cohort" | "video", now: number, before: number) => {
    const ok = kind === "website" ? data.compare.website
      : kind === "cohort" ? data.compare.cohort
      : data.compare.cohort && trackedSince(data.video.since);
    return ok ? { now, prev: before } : {};
  };

  return (
    <div ref={wrap} className="relative">
      <svg className="pointer-events-none absolute inset-0" width={size.w} height={size.h} aria-hidden>
        <defs>
          {/* userSpaceOnUse: a straight vertical connector has a zero-width
              box, and a box-relative gradient would paint nothing on it. */}
          <linearGradient id="ffm-flow" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2={Math.max(1, size.h)}>
            <stop offset="0%" stopColor="#818cf8" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#e879f9" stopOpacity="0.45" />
          </linearGradient>
        </defs>
        {geom.map((g) => (
          <path
            key={g.key}
            d={g.d}
            fill="none"
            stroke={g.color === "#818cf8" && !g.dashed ? "url(#ffm-flow)" : g.color}
            strokeOpacity={g.dashed ? 0.5 : g.color === "#818cf8" ? 1 : 0.55}
            strokeWidth={g.w}
            strokeLinecap="round"
            strokeDasharray={g.dashed ? "5 6" : undefined}
          />
        ))}
      </svg>
      {geom.filter((g) => g.label).map((g) => (
        <span
          key={`l:${g.key}`}
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full border border-zinc-800 bg-zinc-950/90 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-zinc-300 shadow-sm"
          style={{ left: g.lx, top: g.ly }}
        >
          {g.label}
        </span>
      ))}

      {/* Traffic: where people came from. Click one to see only its people. */}
      <div className="relative">
        <div className="mb-2 flex items-center justify-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500">
          {data.source ? (
            <>
              <span>Traffic from {data.source.name}</span>
              <button type="button" onClick={() => onPickSource(null)} className="rounded-md px-1.5 py-0.5 normal-case tracking-normal text-indigo-300 hover:bg-indigo-500/10">Show all traffic</button>
            </>
          ) : <span>Where they came from · {periodLabel}</span>}
        </div>
        <div ref={reg("traffic-row")} className="flex flex-wrap justify-center gap-2">
          {traffic.length === 0 && <p className="py-3 text-sm text-zinc-500">No visits or signups in this period.</p>}
          {traffic.map((t) => (
            <button
              key={t.id}
              ref={reg(`src:${t.id}`)}
              type="button"
              disabled={!t.slug}
              onClick={() => t.slug && onPickSource(data.source?.slug === t.slug ? null : t.slug)}
              title={t.slug ? `Show only ${t.name}` : undefined}
              className={cn(
                "cc-card min-w-[9.5rem] px-3 py-2 text-left transition-colors",
                t.slug ? "cursor-pointer hover:ring-1 hover:ring-indigo-400/40" : "cursor-default",
                data.source?.slug === t.slug && "ring-1 ring-indigo-400/50",
              )}
            >
              <span className="flex items-center gap-1.5 text-[12px] font-medium text-zinc-100">
                <span className={cn("h-2 w-2 shrink-0 rounded-full", dotClass(t.color))} />
                <span className="truncate">{t.name}</span>
              </span>
              <span className="mt-0.5 block text-[11px] tabular-nums text-zinc-500">
                {num(t.visitors)} visitors · <span className="text-zinc-300">{num(t.signups)} signups</span>
              </span>
            </button>
          ))}
          {moreTraffic > 0 && <span className="self-center text-[11px] text-zinc-500">+{moreTraffic} more</span>}
        </div>
      </div>

      {/* The lanes. Main lane in the middle column, agencies on the right. */}
      <div className="relative mt-14 grid grid-cols-1 gap-y-14 lg:grid-cols-[1fr_minmax(0,21rem)_1fr] lg:gap-x-10">
        <Node id="visited" reg={reg} className="lg:col-start-2 lg:row-start-1" icon={Globe} title="Visited syncsuite.co" value={num(data.visitors)} {...cmp("website", data.visitors, prev.visitors)} sub="different people on the website" />
        <Node id="opened" reg={reg} className="lg:col-start-2 lg:row-start-2" icon={MousePointerClick} title="Opened signup" value={num(data.started_signup)} {...cmp("website", data.started_signup, prev.started_signup)} sub="went to the Free or Pro signup page" />
        <Node
          id="signed" reg={reg} className="lg:col-start-2 lg:row-start-3" icon={UserPlus} title="Signed up" value={num(s.total)} {...cmp("cohort", s.total, prev.signups)}
          sub={data.door === "all" ? "new SyncSuite accounts" : data.door === "free" ? "through the Free door" : "through the Pro door"}
          onClick={() => onPeople("signups")} tone="brand"
        >
          {data.door === "all" && (
            <div className="mt-3 grid grid-cols-2 gap-1.5">
              <SubStat label="Free door" value={s.free} of={s.total} onClick={() => onPeople("free")} />
              <SubStat label="Pro door" value={s.pro} of={s.total} onClick={() => onPeople("pro")} />
            </div>
          )}
        </Node>
        <Node
          id="video" reg={reg} className="lg:col-start-2 lg:row-start-4" icon={PlayCircle} title="Watched the video" value={num(data.video.started)} {...cmp("video", data.video.started, prev.video)}
          sub="the quick start video" onClick={() => onPeople("video")}
          foot={<>{sinceLabel(data.video.since)}{videoNote ? <><br />{videoNote}</> : null}</>}
        >
          <div className="mt-3 space-y-1.5">
            <Meter label="Pressed play" value={data.video.started} of={s.total} onClick={() => onPeople("video")} />
            <Meter label="Watched half" value={data.video.half} of={s.total} onClick={() => onPeople("video_half")} />
            <Meter label="Watched it all" value={data.video.finished} of={s.total} onClick={() => onPeople("video_finished")} />
          </div>
        </Node>
        <Node
          id="paying" reg={reg} className="lg:col-start-2 lg:row-start-5" icon={CreditCard} title="Paying for Pro" value={num(data.paying.n)} {...cmp("cohort", data.paying.n, prev.paying)}
          sub={data.paying.revenue > 0 ? `${usd(data.paying.revenue)} collected from them so far` : "made a first payment"} onClick={() => onPeople("paying")} tone="good"
        />

        {/* The offer ladder, one row per step (two masterminds sit side by side). */}
        {depthRows.map((row, i) => (
          <div key={i} className="flex flex-wrap justify-center gap-4 lg:col-span-3">
            {row.map((o) => (
              <OfferNode key={o.id} o={o} reg={reg} feeder={offerName(o.feeds_from)} signups={s.total} onPeople={onPeople} />
            ))}
          </div>
        ))}

        {/* Agencies: their own video, then the done-for-you offer. */}
        <Node
          id="agencies" reg={reg} className="order-last lg:order-none lg:col-start-3 lg:row-start-3" icon={Building2} title="Agencies" value={num(s.agencies)}
          sub="signed up as an agency" onClick={() => onPeople("agencies")}
        />
        <Node
          id="agency-video" reg={reg} className="order-last lg:order-none lg:col-start-3 lg:row-start-4" icon={Clapperboard} title="Agency video" value={null}
          state="planned" sub="Agencies skip the main video and get one made for them, which sells the done-for-you offer."
        />
        {agencyOffers.map((o, i) => (
          <div key={o.id} className="order-last flex justify-center lg:order-none lg:col-start-3" style={wideRow(5 + i)}>
            <OfferNode o={o} reg={reg} feeder={offerName(o.feeds_from)} signups={s.total} onPeople={onPeople} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Pin an agency-lane card to a grid row on wide screens only (phones stack it last). */
function wideRow(row: number): React.CSSProperties | undefined {
  return typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches ? { gridRowStart: row } : undefined;
}

// ─── Cards ───────────────────────────────────────────────────────────────────

function Node({
  id, reg, className, icon: Icon, title, value, sub, foot, now, prev, onClick, children, tone, state = "live",
}: {
  id: string;
  reg: (id: string) => (el: HTMLElement | null) => void;
  className?: string;
  icon: ComponentType<{ className?: string }>;
  title: string;
  value: ReactNode | null;
  sub?: ReactNode;
  foot?: ReactNode;
  now?: number;
  prev?: number;
  onClick?: () => void;
  children?: ReactNode;
  tone?: "brand" | "good";
  state?: "live" | "untracked" | "planned";
}) {
  const muted = state !== "live";
  return (
    <div className={cn("relative z-[1] mx-auto w-full max-w-sm", className)}>
      <div
        ref={reg(id)}
        className={cn(
          "rounded-2xl p-4",
          muted ? "border border-dashed border-zinc-700 bg-zinc-950/70" : "cc-card",
          tone === "brand" && !muted && "cc-glow",
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
            <Icon className={cn("h-3.5 w-3.5", muted ? "text-zinc-500" : tone === "good" ? "text-emerald-400" : "text-indigo-300")} />
            {title}
          </span>
          {state === "planned" ? <StatePill icon={Lock}>Not made yet</StatePill> : state === "untracked" ? <StatePill>Not tracked yet</StatePill> : now != null && prev != null ? <Delta now={now} prev={prev} /> : null}
        </div>
        {value != null && (
          onClick ? (
            <button type="button" onClick={onClick} className="mt-1 block text-left text-[28px] font-semibold leading-tight tracking-tight tabular-nums text-zinc-50 underline-offset-4 hover:underline" title="See who">
              {value}
            </button>
          ) : (
            <div className="mt-1 text-[28px] font-semibold leading-tight tracking-tight tabular-nums text-zinc-50">{value}</div>
          )
        )}
        {sub && <p className={cn("text-xs", muted ? "mt-1.5 leading-relaxed text-zinc-500" : "text-zinc-500")}>{sub}</p>}
        {children}
        {foot && <p className="mt-2.5 border-t border-zinc-800/60 pt-2 text-[11px] leading-relaxed text-zinc-500">{foot}</p>}
      </div>
    </div>
  );
}

function OfferNode({ o, reg, feeder, signups, onPeople }: { o: Offer; reg: (id: string) => (el: HTMLElement | null) => void; feeder: string | null; signups: number; onPeople: (s: Step) => void }) {
  const Icon = o.kind === "premium" ? Crown : o.kind === "event" ? CalendarDays : Gift;
  const price = o.price_usd > 0 ? `${usd(o.price_usd)}${o.recurring ? `/${o.recurring === "monthly" ? "mo" : o.recurring}` : ""}` : "Free";
  const goal = o.target_rate != null ? Math.round(o.target_rate * 1000) / 10 : null;
  const feedsFromSignup = !!feeder && /free/i.test(feeder);
  const goalText = goal != null
    ? feedsFromSignup
      ? `Goal: ${goal}% of signups${signups ? ` (about ${Math.round(o.target_rate! * signups * 10) / 10} of ${signups} in this period)` : ""}`
      : `Goal: ${goal}% of ${feeder ?? "the step before"}`
    : null;
  return (
    <div className="relative z-[1] w-full max-w-[17rem]">
      <div ref={reg(`offer:${o.id}`)} className={cn("rounded-2xl p-4", o.count == null ? "border border-dashed border-zinc-700 bg-zinc-950/70" : "cc-card")}>
        <div className="flex items-start justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
            <Icon className={cn("h-3.5 w-3.5 shrink-0", o.kind === "premium" ? "text-amber-300" : "text-fuchsia-300")} />
            <span className="truncate">{o.name}</span>
          </span>
          <span className="shrink-0 text-sm font-semibold tabular-nums text-zinc-100">{price}</span>
        </div>
        {o.count == null ? (
          <div className="mt-2"><StatePill>Not tracked yet</StatePill></div>
        ) : o.code ? (
          <button type="button" onClick={() => onPeople(`offer:${o.code}`)} title="See who" className="mt-1 block text-left text-[28px] font-semibold leading-tight tabular-nums text-zinc-50 underline-offset-4 hover:underline">
            {num(o.count)}
          </button>
        ) : (
          <div className="mt-1 text-[28px] font-semibold leading-tight tabular-nums text-zinc-50">{num(o.count)}</div>
        )}
        {o.count != null && o.kind === "event" && <p className="text-xs text-zinc-500">came to it</p>}
        {o.count != null && o.code && o.kind !== "event" && o.kind !== "free" && <p className="text-xs text-zinc-500">bought it</p>}
        {o.included != null && o.included > 0 && (
          <button type="button" onClick={() => onPeople("offer:dfy_month_included")} className="mt-1 block text-left text-xs text-indigo-300 underline-offset-4 hover:underline">
            + {num(o.included)} with Pro yearly
          </button>
        )}
        {goalText && <p className="mt-2 text-[11px] leading-relaxed text-zinc-500">{goalText}</p>}
      </div>
    </div>
  );
}

function StatePill({ children, icon: Icon }: { children: ReactNode; icon?: ComponentType<{ className?: string }> }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-zinc-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
      {Icon && <Icon className="h-2.5 w-2.5" />}
      {children}
    </span>
  );
}

function SubStat({ label, value, of, onClick }: { label: string; value: number; of: number; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="rounded-xl border border-zinc-800 bg-zinc-900/50 px-2.5 py-1.5 text-left transition-colors hover:border-indigo-400/40">
      <span className="block text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</span>
      <span className="text-sm font-semibold tabular-nums text-zinc-100">{num(value)}</span>
      <span className="ml-1 text-[11px] tabular-nums text-zinc-500">{pctLabel(value, of)}</span>
    </button>
  );
}

function Meter({ label, value, of, onClick }: { label: string; value: number; of: number; onClick: () => void }) {
  const p = of ? Math.min(100, (value / of) * 100) : 0;
  return (
    <button type="button" onClick={onClick} className="group block w-full text-left">
      <span className="flex items-center justify-between text-[11px]">
        <span className="text-zinc-400 group-hover:text-zinc-200">{label}</span>
        <span className="tabular-nums text-zinc-300">{num(value)} <span className="text-zinc-500">· {pctLabel(value, of)}</span></span>
      </span>
      <span className="mt-1 block h-1.5 overflow-hidden rounded-full bg-zinc-800">
        <span className="block h-full rounded-full cc-brand-bg" style={{ width: `${value > 0 ? Math.max(3, p) : 0}%` }} />
      </span>
    </button>
  );
}

// ─── Who's behind a number ───────────────────────────────────────────────────

function PeopleDrawer({ step, title, onClose, query, backState }: { step: Step | null; title: string; onClose: () => void; query: Record<string, string>; backState: { from: string } }) {
  const [rows, setRows] = useState<Person[] | null>(null);
  const [total, setTotal] = useState(0);
  const key = JSON.stringify(query);
  useEffect(() => {
    if (!step) return;
    let live = true;
    setRows(null);
    api.get<{ people: Person[]; total: number }>("/command/funnel-map/people", { step, ...JSON.parse(key) })
      .then((d) => { if (live) { setRows(d.people); setTotal(d.total); } })
      .catch(() => { if (live) { setRows([]); setTotal(0); } });
    return () => { live = false; };
  }, [step, key]);

  const detail = (p: Person): ReactNode => {
    if (step === "video" || step === "video_half" || step === "video_finished") {
      const v = p.video_step ?? 0;
      return v >= 90 ? "Watched it all" : v > 0 ? `Watched ${v}%` : "Pressed play";
    }
    if (step === "paying") return p.revenue > 0 ? usd(p.revenue) : "Paying";
    if (step?.startsWith("offer:")) return p.revenue > 0 ? `${usd(p.revenue)} paid` : p.door === "pro" ? "Pro door" : "Free door";
    return p.door === "pro" ? "Pro door" : "Free door";
  };

  return (
    <Drawer open={!!step} onClose={onClose} title={title} width="md">
      {!rows ? (
        <div className="space-y-2 p-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : rows.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-zinc-500">Nobody yet for these filters.</p>
      ) : (
        <div className="px-4 py-3">
          <p className="mb-2 px-1 text-xs text-zinc-500">{num(total)} {total === 1 ? "person" : "people"}, newest signup first.</p>
          <ul className="divide-y divide-zinc-800/60">
            {rows.map((p) => (
              <li key={p.account_id}>
                <Link
                  to={p.contact_id ? `/contacts/${p.contact_id}` : `/accounts/${p.account_id}`}
                  state={backState}
                  onClick={onClose}
                  className="flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-zinc-900/60"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-zinc-100">{p.name}</span>
                    <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-zinc-500">
                      {p.source_name && <><span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", dotClass(p.source_color))} /><span className="truncate">{p.source_name}</span><span>·</span></>}
                      <span className="shrink-0">signed up {timeAgo(p.signed_up_at)}</span>
                    </span>
                  </span>
                  <span className="shrink-0 text-xs font-medium tabular-nums text-zinc-300">{detail(p)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Drawer>
  );
}
